/**
 * Señales técnicas (contrato 3.1, grupo «tecnico»):
 * - `app_error`: errores de JavaScript sin capturar (`error`) y promesas
 *   rechazadas sin `catch` (`unhandledrejection`). Solo la clase del error
 *   (`error.name`, en minúsculas para cumplir el tipo slug: `typeerror`),
 *   nunca el mensaje. Un `ChunkLoadError` (no se pudo descargar un trozo del
 *   bundle, típico tras un despliegue) es `chunk_load`. Los errores de scripts
 *   de otro origen («Script error.», sin objeto de error) no se cuentan. El
 *   tracker deja como mucho 5 por página.
 * - `api_slow`: llamadas al backend de más de 3 s (las mide la capa HTTP
 *   común, `api/core/requestTiming`).
 * - `web_vital`: una por métrica y carga, con `web-vitals` (v1: LCP, FID, CLS,
 *   FCP y TTFB; INP no existe en la v1).
 */
import { AppErrorKind } from "./events";
import { redactPath } from "./redact";

export interface ErrorWatcherTarget {
  appError: (kind: AppErrorKind, name: string | null) => void;
}

export interface TimingTarget {
  apiTiming: (endpoint: string, ms: number) => void;
}

export interface VitalTarget {
  webVital: (name: string, value: number) => void;
}

/** Clase del error como slug (`TypeError` → `typeerror`), o null. */
export const errorNameOf = (error: unknown): string | null => {
  if (!error || (typeof error !== "object" && typeof error !== "function")) return null;
  const name = (error as { name?: unknown }).name;
  if (typeof name !== "string" || !name) return null;
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9_.-]/g, "_")
    .replace(/^[^a-z0-9]+/, "")
    .slice(0, 64);
  return slug || null;
};

/** Tipo y nombre de un error sin capturar; null si no hay error que contar. */
export const appErrorOf = (
  source: "error" | "unhandled_rejection",
  error: unknown
): { kind: AppErrorKind; name: string | null } | null => {
  const name = errorNameOf(error);
  if (name === "chunkloaderror") return { kind: "chunk_load", name };
  return { kind: source, name };
};

export const installErrorWatchers = (
  target: ErrorWatcherTarget,
  win: Window = window
): (() => void) => {
  const onError = (event: ErrorEvent) => {
    try {
      // «Script error.» de otro origen: sin objeto de error, no es nuestro.
      if (!event.error) return;
      const found = appErrorOf("error", event.error);
      if (found) target.appError(found.kind, found.name);
    } catch {
      /* el tracking nunca rompe la app */
    }
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    try {
      const found = appErrorOf("unhandled_rejection", event.reason);
      if (found) target.appError(found.kind, found.name);
    } catch {
      /* noop */
    }
  };
  win.addEventListener("error", onError);
  win.addEventListener("unhandledrejection", onRejection);
  return () => {
    win.removeEventListener("error", onError);
    win.removeEventListener("unhandledrejection", onRejection);
  };
};

/** Ruta de una llamada al backend, sin host ni query y redactada. */
export const apiEndpointOf = (url: string, base?: string): string | null => {
  try {
    const origin =
      base ?? (typeof window !== "undefined" ? window.location.origin : "http://localhost");
    return redactPath(new URL(url, origin).pathname);
  } catch {
    return null;
  }
};

/** Lo que da la capa HTTP: URL y duración de cada llamada. */
export const reportApiTiming = (target: TimingTarget, url: string, ms: number): void => {
  const endpoint = apiEndpointOf(url);
  if (endpoint) target.apiTiming(endpoint, ms);
};

interface Metric {
  name: string;
  value: number;
}
type Getter = (report: (metric: Metric) => void) => void;

export interface WebVitalsLib {
  getCLS: Getter;
  getFID: Getter;
  getLCP: Getter;
  getFCP: Getter;
  getTTFB: Getter;
}

let vitalsInstalled = false;

/**
 * Engancha las métricas de `web-vitals` una vez por carga de página (la
 * librería no se puede desenganchar). Cada métrica llega una vez; el tracker
 * descarta repeticiones igualmente.
 */
export const installWebVitals = (target: VitalTarget, lib?: WebVitalsLib): void => {
  if (vitalsInstalled) return;
  vitalsInstalled = true;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const vitals: WebVitalsLib = lib ?? require("web-vitals");
    const report = (metric: Metric) => {
      try {
        target.webVital(metric.name, metric.value);
      } catch {
        /* noop */
      }
    };
    [vitals.getCLS, vitals.getFID, vitals.getLCP, vitals.getFCP, vitals.getTTFB].forEach((get) => {
      try {
        get(report);
      } catch {
        /* navegador sin PerformanceObserver: esa métrica no sale */
      }
    });
  } catch {
    /* sin web-vitals: no hay métricas, no rompe nada */
  }
};

/** Solo para tests. */
export const resetWebVitalsForTests = (): void => {
  vitalsInstalled = false;
};
